// src/pages/Opportunities.jsx
import { useState, useEffect } from 'react';
import { collection, query, where, onSnapshot, getDocs } from 'firebase/firestore';
import { db }          from '../config/firebase';
import { useUIStore }  from '../store/uiStore';
import { useAuth }     from '../hooks/useAuth';
import { toast }       from 'react-hot-toast';
import Footer          from '../components/layout/Footer';
import { C }           from '../styles/tokens';
import { submitApplication } from '../services/applicationService';

const FILTERS    = ['All', 'Remote', 'Urgent', 'Featured', 'High Pay'];
const CATEGORIES = ['All Categories', 'Design & Creative', 'Engineering', 'Product', 'Marketing', 'Finance', 'Operations', 'Data & AI', 'Customer Success'];
const CAT_ICON   = { 'Design & Creative': '🎨', Engineering: '⚙️', Product: '📦', Marketing: '📈', Finance: '💰', Operations: '🔧', 'Data & AI': '🤖', 'Customer Success': '🤝' };

function postedAgo(ts) {
  if (!ts?.toMillis) return 'Recently';
  const h = Math.floor((Date.now() - ts.toMillis()) / 3600000);
  if (h < 1) return 'Just now';
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

function payLabel(j) {
  if (j.payDisplay)         return j.payDisplay;
  if (j.payMin && j.payMax) return `${j.payMin}–${j.payMax} ONT/mo`;
  if (j.payMin)             return `${j.payMin}+ ONT/mo`;
  return 'Negotiable';
}

// ── Job Card ──────────────────────────────────────────────────────────
function JobCard({ job: j, isSelected, onClick }) {
  const icon = CAT_ICON[j.category] || '💼';
  return (
    <div
      className="card"
      onClick={onClick}
      style={{ padding: 22, cursor: 'pointer', border: isSelected ? '1px solid rgba(254,52,232,0.5)' : '1px solid rgba(254,52,232,0.1)', transition: 'all .25s', background: isSelected ? 'rgba(254,52,232,0.04)' : undefined }}
      onMouseEnter={e => { if (!isSelected) { e.currentTarget.style.borderColor='rgba(254,52,232,.3)'; e.currentTarget.style.transform='translateY(-3px)'; e.currentTarget.style.boxShadow='0 16px 48px rgba(95,8,231,.2)'; }}}
      onMouseLeave={e => { if (!isSelected) { e.currentTarget.style.borderColor='rgba(254,52,232,.1)'; e.currentTarget.style.transform='translateY(0)'; e.currentTarget.style.boxShadow='none'; }}}
    >
      <div style={{ position:'absolute', top:0, left:0, right:0, height:2, background: j.featured ? `linear-gradient(90deg,${C.magenta},${C.violet})` : j.urgent ? `linear-gradient(90deg,${C.danger},${C.warning})` : `linear-gradient(90deg,${C.violet}60,transparent)` }} />
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:12 }}>
        <div style={{ display:'flex', gap:12, alignItems:'center' }}>
          <div style={{ width:46, height:46, borderRadius:12, background:'rgba(255,255,255,.06)', border:'1px solid rgba(255,255,255,.08)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:22, flexShrink:0 }}>{icon}</div>
          <div>
            <div className="syne" style={{ fontSize:15, fontWeight:700, marginBottom:2 }}>{j.title}</div>
            <div style={{ fontSize:12, color:'rgba(255,255,255,.45)' }}>{j.company || j.collaboratorName} · {j.location}</div>
          </div>
        </div>
        <div style={{ display:'flex', flexDirection:'column', gap:4, alignItems:'flex-end' }}>
          {j.featured && <span style={{ fontSize:10, padding:'2px 8px', borderRadius:100, background:'rgba(254,52,232,0.15)', color:C.magenta, fontWeight:700, whiteSpace:'nowrap' }}>⭐ Featured</span>}
          {j.urgent   && <span style={{ fontSize:10, padding:'2px 8px', borderRadius:100, background:'rgba(239,68,68,0.15)',  color:C.danger,  fontWeight:700, whiteSpace:'nowrap' }}>🔥 Urgent</span>}
        </div>
      </div>
      <div style={{ display:'flex', gap:6, flexWrap:'wrap', marginBottom:14 }}>
        {(j.skills||[]).slice(0,4).map(t => <span key={t} style={{ fontSize:11, padding:'3px 9px', borderRadius:6, background:'rgba(255,255,255,.05)', border:'1px solid rgba(255,255,255,.08)', color:'rgba(255,255,255,.55)' }}>{t}</span>)}
        {(j.skills?.length||0)>4 && <span style={{ fontSize:11, color:'rgba(255,255,255,0.3)', padding:'3px 0' }}>+{j.skills.length-4} more</span>}
      </div>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
          <span style={{ fontSize:11, padding:'3px 10px', borderRadius:100, background:'rgba(255,255,255,0.06)', color:'rgba(255,255,255,0.55)', fontWeight:600 }}>{j.type||'Full-time'}</span>
          <span style={{ fontSize:11, color:'rgba(255,255,255,.35)' }}>{postedAgo(j.createdAt)}</span>
        </div>
        <span style={{ fontSize:13, fontWeight:700, color:C.success, fontFamily:"'Syne',sans-serif" }}>{payLabel(j)}</span>
      </div>
    </div>
  );
}

// ── Detail Panel ──────────────────────────────────────────────────────
function DetailPanel({ job: j, applied, applying, onApply, onClose, user, openAuthModal }) {
  const icon = CAT_ICON[j.category] || '💼';
  const isApplied  = applied.has(j.id);

  return (
    <div className="opp-detail-panel card" style={{ padding:28, position:'sticky', top:80 }}>
      <div style={{ position:'absolute', top:0, left:0, right:0, height:3, background:`linear-gradient(90deg,${C.magenta},${C.violet})` }} />
      <button onClick={onClose} style={{ position:'absolute', top:14, right:14, background:'rgba(255,255,255,.07)', border:'none', color:'white', width:30, height:30, borderRadius:7, cursor:'pointer', fontSize:13, display:'flex', alignItems:'center', justifyContent:'center' }}>✕</button>

      <div style={{ width:56, height:56, borderRadius:14, background:'rgba(255,255,255,.07)', border:'1px solid rgba(255,255,255,.08)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:28, marginBottom:14 }}>{icon}</div>
      <h2 className="syne" style={{ fontSize:20, fontWeight:800, marginBottom:4 }}>{j.title}</h2>
      <p style={{ fontSize:13, color:'rgba(255,255,255,.45)', marginBottom:8 }}>{j.company||j.collaboratorName} · {j.location}</p>

      <div style={{ display:'flex', gap:6, flexWrap:'wrap', marginBottom:16 }}>
        {j.type     && <span style={{ fontSize:11, padding:'3px 10px', borderRadius:100, background:'rgba(255,255,255,0.06)', color:'rgba(255,255,255,0.55)', fontWeight:600 }}>{j.type}</span>}
        {j.category && <span style={{ fontSize:11, padding:'3px 10px', borderRadius:100, background:'rgba(95,8,231,0.15)', color:'#a78bfa', fontWeight:600 }}>{j.category}</span>}
        {j.featured && <span style={{ fontSize:11, padding:'3px 10px', borderRadius:100, background:'rgba(254,52,232,0.12)', color:C.magenta, fontWeight:700 }}>⭐ Featured</span>}
        {j.urgent   && <span style={{ fontSize:11, padding:'3px 10px', borderRadius:100, background:'rgba(239,68,68,0.12)',  color:C.danger,  fontWeight:700 }}>🔥 Urgent</span>}
      </div>

      <div style={{ padding:'14px', borderRadius:12, background:'rgba(0,208,132,0.08)', border:'1px solid rgba(0,208,132,0.15)', textAlign:'center', marginBottom:18 }}>
        <div className="syne" style={{ fontSize:22, fontWeight:800, color:C.success }}>{payLabel(j)}</div>
        <div style={{ fontSize:11, color:'rgba(255,255,255,0.4)', marginTop:2 }}>ONT Token · Solana Blockchain</div>
      </div>

      <h4 className="syne" style={{ fontSize:12, fontWeight:700, color:'rgba(255,255,255,.5)', marginBottom:8, letterSpacing:'.06em' }}>ABOUT THIS ROLE</h4>
      <p style={{ fontSize:13.5, color:'rgba(255,255,255,.55)', lineHeight:1.75, marginBottom:16 }}>{j.description||'No description provided.'}</p>

      {(j.skills?.length>0) && <>
        <h4 className="syne" style={{ fontSize:12, fontWeight:700, color:'rgba(255,255,255,.5)', marginBottom:8, letterSpacing:'.06em' }}>REQUIRED SKILLS</h4>
        <div style={{ display:'flex', gap:6, flexWrap:'wrap', marginBottom:20 }}>
          {j.skills.map(t => <span key={t} style={{ fontSize:11, padding:'4px 10px', borderRadius:6, background:'rgba(254,52,232,.1)', border:'1px solid rgba(254,52,232,.2)', color:C.magenta }}>{t}</span>)}
        </div>
      </>}

      <div style={{ fontSize:12, color:'rgba(255,255,255,0.3)', marginBottom:18 }}>
        Posted {postedAgo(j.createdAt)} · {j.applicants||0} applicant{j.applicants!==1?'s':''}
      </div>

      {isApplied ? (
        <div style={{ padding:'14px', borderRadius:9, background:'rgba(0,208,132,0.1)', border:'1px solid rgba(0,208,132,0.25)', textAlign:'center', color:C.success, fontWeight:700, fontSize:14 }}>
          ✅ Application Submitted!
        </div>
      ) : (
        <button
          onClick={() => onApply(j)}
          disabled={applying}
          style={{ width:'100%', padding:'13px', background: applying ? 'rgba(255,255,255,0.1)' : `linear-gradient(135deg,${C.magenta},${C.violet})`, border:'none', borderRadius:9, color:'#fff', fontSize:14, fontWeight:700, cursor: applying ? 'default':'pointer', fontFamily:"'Syne',sans-serif", marginBottom:10, opacity: applying ? 0.7 : 1 }}
        >
          {applying ? 'Submitting…' : user ? 'Apply Now →' : '🔒 Sign Up to Apply →'}
        </button>
      )}
      <button style={{ width:'100%', padding:'11px', background:'rgba(255,255,255,.05)', border:'1px solid rgba(255,255,255,.1)', borderRadius:9, color:'#fff', fontSize:13, fontWeight:600, cursor:'pointer' }}>💾 Save Opportunity</button>
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────
export default function Opportunities() {
  const { openAuthModal } = useUIStore();
  const { user, profile } = useAuth();

  const [jobs,     setJobs]     = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [search,   setSearch]   = useState('');
  const [filter,   setFilter]   = useState('All');
  const [cat,      setCat]      = useState('All Categories');
  const [selected, setSelected] = useState(null);
  const [applied,  setApplied]  = useState(new Set()); // opportunityIds user has applied to
  const [applying, setApplying] = useState(false);     // submit button loading state

  /* Live-subscribe to live opportunities */
  useEffect(() => {
    const q = query(collection(db, 'opportunities'), where('status', '==', 'live'));
    const unsub = onSnapshot(q, snap => {
      const docs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      docs.sort((a, b) => {
        if (a.featured && !b.featured) return -1;
        if (!a.featured && b.featured) return  1;
        if (a.urgent   && !b.urgent)   return -1;
        if (!a.urgent   && b.urgent)   return  1;
        return (b.createdAt?.toMillis?.() || 0) - (a.createdAt?.toMillis?.() || 0);
      });
      setJobs(docs);
      setLoading(false);
    }, err => { console.error(err); setLoading(false); });
    return () => unsub();
  }, []);

  /* Load jobs user has already applied to (so button shows correctly on revisit) */
  useEffect(() => {
    if (!user) { setApplied(new Set()); return; }
    getDocs(query(collection(db, 'applications'), where('userId', '==', user.uid)))
      .then(snap => setApplied(new Set(snap.docs.map(d => d.data().opportunityId))))
      .catch(() => {});
  }, [user]);

  /* Keep selected job in sync with live updates */
  useEffect(() => {
    if (!selected) return;
    const updated = jobs.find(j => j.id === selected.id);
    if (updated) setSelected(updated);
  }, [jobs]);

  /* Apply handler — writes to Firestore */
  const handleApply = async (job) => {
    if (!user) {
      toast('Sign in to apply', { icon: '🔒' });
      openAuthModal('register');
      return;
    }
    setApplying(true);
    try {
      await submitApplication(user, profile, job);
      setApplied(prev => new Set([...prev, job.id]));
      toast.success(`Application submitted for ${job.title}! 🎉`);
    } catch (e) {
      if (e.message === 'already_applied') {
        setApplied(prev => new Set([...prev, job.id]));
        toast('You have already applied for this role', { icon: 'ℹ️' });
      } else {
        toast.error(e.message || 'Failed to submit — please try again');
      }
    } finally {
      setApplying(false);
    }
  };

  const filtered = jobs.filter(j => {
    const q = search.toLowerCase();
    const matchSearch = !q ||
      j.title?.toLowerCase().includes(q) ||
      j.company?.toLowerCase().includes(q) ||
      j.collaboratorName?.toLowerCase().includes(q) ||
      j.location?.toLowerCase().includes(q) ||
      (j.skills||[]).some(t => t.toLowerCase().includes(q));
    const matchFilter =
      filter === 'All' ||
      (filter === 'Remote'   && j.location?.toLowerCase().includes('remote')) ||
      (filter === 'Urgent'   && j.urgent) ||
      (filter === 'Featured' && j.featured) ||
      (filter === 'High Pay' && (Math.max(j.payMax||0, j.payMin||0) >= 200));
    const matchCat = cat === 'All Categories' || j.category === cat;
    return matchSearch && matchFilter && matchCat;
  });

  return (
    <div style={{ background: C.bg, paddingTop: 64 }}>
      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        .opp-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(290px,1fr)); gap: 16px; }
        .opp-layout { display: grid; gap: 20px; align-items: start; }
        @media (max-width: 768px) {
          .opp-detail-panel { position:fixed !important; top:64px !important; left:0 !important; right:0 !important; bottom:0 !important; z-index:200 !important; overflow-y:auto !important; border-radius:0 !important; background:#0f0f24 !important; animation:fadeIn .2s ease !important; }
          .opp-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>

      {/* Header */}
      <div style={{ background:`linear-gradient(180deg,${C.navy}50,transparent)`, padding:'clamp(36px,6vw,52px) 0 clamp(24px,4vw,36px)', position:'relative' }}>
        <div className="grid-bg" style={{ opacity:.4 }} />
        <div className="container" style={{ position:'relative', zIndex:1, padding:'0 clamp(16px,4vw,28px)' }}>
          <span className="pill" style={{ background:'rgba(254,52,232,.1)', border:'1px solid rgba(254,52,232,.25)', color:C.magenta, marginBottom:18, display:'inline-flex' }}>🌐 Live Opportunities</span>
          <h1 className="syne" style={{ fontSize:'clamp(2rem,4vw,3rem)', fontWeight:800, letterSpacing:'-0.03em', marginBottom:12 }}>
            Find Your Next <span className="grad-text">Opportunity</span>
          </h1>
          <p style={{ color:'rgba(255,255,255,.48)', fontSize:15, marginBottom:24 }}>
            {loading ? 'Loading listings…' : `${filtered.length} verified listing${filtered.length!==1?'s':''}`} · Paid in ONT Tokens
          </p>

          <div style={{ display:'flex', gap:12, flexWrap:'wrap', marginBottom:14 }}>
            <div style={{ position:'relative', flex:1, minWidth:220 }}>
              <span style={{ position:'absolute', left:14, top:'50%', transform:'translateY(-50%)', fontSize:16, color:'rgba(255,255,255,.3)', pointerEvents:'none' }}>🔍</span>
              <input placeholder="Search roles, companies, skills…" value={search} onChange={e => setSearch(e.target.value)}
                style={{ width:'100%', padding:'12px 16px 12px 42px', background:'rgba(255,255,255,.04)', border:'1px solid rgba(255,255,255,.09)', borderRadius:10, color:'#fff', fontSize:14, outline:'none', boxSizing:'border-box' }}
                onFocus={e => e.target.style.borderColor=C.magenta}
                onBlur={e  => e.target.style.borderColor='rgba(255,255,255,.09)'} />
            </div>
            <select value={cat} onChange={e => setCat(e.target.value)} style={{ padding:'12px 16px', background:'#0f0f24', border:'1px solid rgba(255,255,255,.1)', borderRadius:10, color:'white', fontSize:14, outline:'none', cursor:'pointer' }}>
              {CATEGORIES.map(c => <option key={c}>{c}</option>)}
            </select>
          </div>

          <div style={{ display:'flex', gap:8, flexWrap:'wrap', marginBottom:10 }}>
            {FILTERS.map(f => (
              <button key={f} onClick={() => setFilter(f)} style={{ padding:'7px 16px', borderRadius:100, background: filter===f ? `linear-gradient(135deg,${C.magenta},${C.violet})` : 'rgba(255,255,255,.05)', border: filter===f ? 'none' : '1px solid rgba(255,255,255,.1)', color:'#fff', fontSize:12, fontWeight:700, cursor:'pointer', transition:'all .2s', fontFamily:"'Syne',sans-serif" }}>{f}</button>
            ))}
          </div>

          {!user && (
            <div style={{ marginTop:14, padding:'12px 16px', borderRadius:10, background:'rgba(254,52,232,0.06)', border:'1px solid rgba(254,52,232,0.15)', display:'flex', alignItems:'center', justifyContent:'space-between', gap:12, flexWrap:'wrap' }}>
              <span style={{ fontSize:13, color:'rgba(255,255,255,0.6)' }}>🔒 <strong style={{ color:'#fff' }}>Create a free account</strong> to apply and track your applications</span>
              <button onClick={() => openAuthModal('register')} style={{ padding:'8px 18px', background:`linear-gradient(135deg,${C.magenta},${C.violet})`, border:'none', borderRadius:8, color:'#fff', fontSize:12, fontWeight:700, cursor:'pointer', whiteSpace:'nowrap', fontFamily:"'Syne',sans-serif" }}>Sign Up Free →</button>
            </div>
          )}
        </div>
      </div>

      {/* Grid + Detail */}
      <div className="container" style={{ padding:'clamp(20px,3vw,28px) clamp(16px,4vw,28px) 60px' }}>
        <div className="opp-layout" style={{ gridTemplateColumns: selected ? 'clamp(300px,55%,900px) 380px' : '1fr' }}>
          <div className="opp-grid">
            {loading && (
              <div style={{ gridColumn:'1/-1', textAlign:'center', padding:'80px 0', color:'rgba(255,255,255,0.3)' }}>
                <div style={{ width:36, height:36, border:`3px solid rgba(255,255,255,0.1)`, borderTopColor:C.magenta, borderRadius:'50%', animation:'spin .8s linear infinite', margin:'0 auto 16px' }} />
                Loading opportunities…
              </div>
            )}
            {!loading && filtered.length===0 && (
              <div style={{ gridColumn:'1/-1', textAlign:'center', padding:'80px 0', color:'rgba(255,255,255,.35)', fontSize:15 }}>
                <div style={{ fontSize:40, marginBottom:12 }}>🔍</div>
                {jobs.length===0 ? 'No live opportunities yet — check back soon!' : 'No results match your search.'}
              </div>
            )}
            {!loading && filtered.map(j => (
              <JobCard key={j.id} job={j} isSelected={selected?.id===j.id} onClick={() => setSelected(selected?.id===j.id ? null : j)} />
            ))}
          </div>

          {selected && (
            <DetailPanel
              job={selected}
              applied={applied}
              applying={applying}
              onApply={handleApply}
              onClose={() => setSelected(null)}
              user={user}
              openAuthModal={openAuthModal}
            />
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
}
